import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { TrendingUp, TrendingDown, Receipt, Wrench, Star, FileText, Wallet, Clock, CircleDot, Users, ChevronLeft, ChevronRight, BarChart3, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ["", "Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];

const KpiDashboardPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadKpi = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/kpi/monthly?year=${year}&month=${month}`);
      setKpi(res.data);
    } catch {
      toast.error("Erreur chargement KPI");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadKpi(); }, [year, month]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const fmt = (v) => (v || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (v) => (v || 0).toLocaleString('fr-CH');

  const Variation = ({ value, suffix = '%' }) => {
    if (!value || value === 0) return <span className="text-xs text-slate-400 flex items-center gap-0.5"><Minus className="w-3 h-3" /> 0{suffix}</span>;
    if (value > 0) return <span className="text-xs text-emerald-600 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" /> +{value}{suffix}</span>;
    return <span className="text-xs text-red-500 flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" /> {value}{suffix}</span>;
  };

  const ProgressBar = ({ value, max, color = 'bg-blue-600' }) => (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, max > 0 ? (value / max * 100) : 0)}%` }} />
    </div>
  );

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
    <div className="space-y-6" data-testid="kpi-dashboard-page">
      {/* Header with month selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tableau de bord KPI</h1>
          <p className="text-sm text-slate-500 mt-0.5">Performance mensuelle du garage</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-2 py-1 shadow-sm">
          <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold text-slate-800 min-w-[140px] text-center" data-testid="current-month">
            {MONTH_NAMES[month]} {year}
          </span>
          <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Chargement des KPI...</div>
      ) : kpi && (
        <>
          {/* ROW 1: Main KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CA */}
            <Card className="p-5 hover:shadow-md transition-shadow" data-testid="kpi-ca">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                </div>
                <Variation value={kpi.chiffre_affaires.variation_pct} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{fmt(kpi.chiffre_affaires.total)}</p>
              <p className="text-xs text-slate-500 mt-0.5">Chiffre d'affaires (CHF)</p>
              <div className="flex gap-3 mt-2 text-xs text-slate-400">
                <span>Factures: {fmt(kpi.chiffre_affaires.invoices)}</span>
                <span>Atelier: {fmt(kpi.chiffre_affaires.atelier)}</span>
              </div>
            </Card>

            {/* Marge nette */}
            <Card className={`p-5 hover:shadow-md transition-shadow ${kpi.marge_nette.positive ? 'border-emerald-200' : 'border-red-200'}`} data-testid="kpi-marge">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${kpi.marge_nette.positive ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  {kpi.marge_nette.positive ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
                </div>
              </div>
              <p className={`text-2xl font-bold ${kpi.marge_nette.positive ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(kpi.marge_nette.current)}</p>
              <p className="text-xs text-slate-500 mt-0.5">Marge nette (CHF)</p>
              <p className="text-xs text-slate-400 mt-1">Mois prec: {fmt(kpi.marge_nette.previous)}</p>
            </Card>

            {/* Fiches de travail */}
            <Card className="p-5 hover:shadow-md transition-shadow" data-testid="kpi-wo">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-blue-600" />
                </div>
                <Variation value={kpi.fiches_travail.variation_pct} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{fmtInt(kpi.fiches_travail.total)}</p>
              <p className="text-xs text-slate-500 mt-0.5">Fiches de travail</p>
              <p className="text-xs text-slate-400 mt-1">{kpi.fiches_travail.completed} terminees</p>
            </Card>

            {/* Satisfaction */}
            <Card className="p-5 hover:shadow-md transition-shadow" data-testid="kpi-satisfaction">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{kpi.satisfaction.average || '-'}<span className="text-sm text-slate-400 font-normal">/5</span></p>
              <p className="text-xs text-slate-500 mt-0.5">Satisfaction clients</p>
              <p className="text-xs text-slate-400 mt-1">{kpi.satisfaction.count} reponses</p>
              {kpi.satisfaction.average > 0 && (
                <ProgressBar value={kpi.satisfaction.average} max={5} color="bg-amber-500" />
              )}
            </Card>
          </div>

          {/* ROW 2: Devis & Depenses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Devis conversion */}
            <Card className="p-5" data-testid="kpi-devis">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-500" /> Devis du mois
                </h3>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  {kpi.devis.conversion_rate}% conversion
                </span>
              </div>
              <div className="flex items-center gap-4 mb-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{kpi.devis.total}</p>
                  <p className="text-xs text-slate-400">Total</p>
                </div>
                <div className="h-10 w-px bg-slate-200" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{kpi.devis.accepted}</p>
                  <p className="text-xs text-slate-400">Acceptes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-500">{kpi.devis.refused}</p>
                  <p className="text-xs text-slate-400">Refuses</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-500">{kpi.devis.pending}</p>
                  <p className="text-xs text-slate-400">{t('common.pending')}</p>
                </div>
              </div>
              {kpi.devis.total > 0 && (
                <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                  {kpi.devis.accepted > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${kpi.devis.accepted / kpi.devis.total * 100}%` }} />}
                  {kpi.devis.refused > 0 && <div className="bg-red-400 transition-all" style={{ width: `${kpi.devis.refused / kpi.devis.total * 100}%` }} />}
                  {kpi.devis.pending > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${kpi.devis.pending / kpi.devis.total * 100}%` }} />}
                </div>
              )}
            </Card>

            {/* Depenses breakdown */}
            <Card className="p-5" data-testid="kpi-depenses">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-red-500" /> Depenses
                </h3>
                <span className="text-lg font-bold text-red-600">{fmt(kpi.depenses.total)} CHF</span>
              </div>
              <div className="flex gap-4 mb-3 text-xs text-slate-500">
                <span>Generales: {fmt(kpi.depenses.generales)}</span>
                <span>Fournisseurs: {fmt(kpi.depenses.fournisseurs)}</span>
              </div>
              {kpi.depenses.breakdown.length > 0 ? (
                <div className="space-y-2">
                  {kpi.depenses.breakdown.slice(0, 5).map((cat, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-slate-600 w-24 truncate">{cat.category}</span>
                      <div className="flex-1">
                        <ProgressBar value={cat.amount} max={kpi.depenses.breakdown[0]?.amount || 1} color="bg-red-400" />
                      </div>
                      <span className="text-xs font-medium text-slate-700 w-20 text-right">{fmt(cat.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 text-center py-4">Aucune depense ce mois</p>
              )}
            </Card>
          </div>

          {/* ROW 3: Heures, Pneus, Clients */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Heures */}
            <Card className="p-5" data-testid="kpi-heures">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{kpi.heures.total}h</p>
                  <p className="text-xs text-slate-500">Heures travaillees</p>
                </div>
              </div>
            </Card>

            {/* Pneus opportunites */}
            <Card className={`p-5 ${kpi.pneus_opportunites.total > 0 ? 'border-amber-200 bg-amber-50/50' : ''}`} data-testid="kpi-pneus">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <CircleDot className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{kpi.pneus_opportunites.total}</p>
                  <p className="text-xs text-slate-500">Pneus a signaler</p>
                </div>
              </div>
              {kpi.pneus_opportunites.total > 0 && (
                <div className="flex gap-3 text-xs mt-1">
                  <span className="text-amber-700">{kpi.pneus_opportunites.moyen} moyen(s)</span>
                  <span className="text-red-600">{kpi.pneus_opportunites.a_remplacer} a remplacer</span>
                </div>
              )}
            </Card>

            {/* Clients */}
            <Card className="p-5" data-testid="kpi-clients">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{fmtInt(kpi.clients.total)}</p>
                  <p className="text-xs text-slate-500">Clients totaux</p>
                </div>
              </div>
              {kpi.clients.nouveaux > 0 && (
                <span className="text-xs text-emerald-600 font-medium">+{kpi.clients.nouveaux} nouveau(x) ce mois</span>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
    </AdminLayout>
  );
};

export default KpiDashboardPage;
